<template lang="pug">
    .multiplayer-game-menu__selectors
        cs-multiselect.selector.examples-type-selector(
            :multiple="false",
            :show-labels="false",
            :searchable="false",
            :close-on-select="true",
            :hide-selected='true',
            :allow-empty="false",
            label="label",
            :value="selectedExamplesType",
            :options="examplesTypes",
            :disabled="gameState === 'started'",
            @select="selectedExamplesType = $event"
        )
        cs-multiselect.selector.amount-of-examples-selector(
            :multiple="false",
            :show-labels="false",
            :searchable="false",
            :close-on-select="true",
            :allow-empty="false",
            v-model="selectedAmountOfExamples",
            :options="amountsOfExamples",
            :disabled="gameState === 'started' || commonParams && (!!commonParams.timeLimit || commonParams.infinityAmountOfExamples)"
        )
            span(slot="inner-label")
                | {{ $i18n.t('trainer.trainer_gameFormSetting_numOfExamples') }}:
                |
        cs-multiselect.selector.infinite-amount-of-examples(
            :options="infiniteOptionsWithoutInitial",
            v-model='selectedInfiniteAmountOfExamplesStatus',
            :placeholder="$i18n.t('trainer.trainer_gameFormSetting_infiniteAmountOfExamples')",
            :multiple="false",
            :allow-empty="false",
            :show-labels="false",
            :searchable="false",
            :close-on-select="true",
            label="label",
            :disabled="gameState === 'started'"
        )
            span(slot="inner-label")
                | {{ $i18n.t('trainer.trainer_gameFormSetting_infiniteAmountOfExamples') }}:
                |
        cs-multiselect.selector.divisible-multiplier-selector(
            :multiple="false",
            :show-labels="false",
            :searchable="false",
            label="value",
            :close-on-select="true",
            :placeholder="$i18n.t('trainer.trainer_gameFormSetting_difficulty')",
            :allow-empty="false",
            v-model="selectedDifficulty",
            :options="difficultyOptions",
            :disabled="gameState === 'started'"
        )
            span(slot="inner-label")
                | {{ $i18n.t('trainer.trainer_gameFormSetting_difficulty') }}:
                |
        cs-multiselect.selector.players-selector(
            :multiple="false",
            :show-labels="false",
            :searchable="false",
            :close-on-select="true",
            :allow-empty="false",
            v-model="playersAmount",
            :options="players",
            :disabled="gameState === 'started'"
        )
            span(slot="inner-label")
                | {{ $i18n.t('trainer.trainer_gameSetting_players') }}:
                |
</template>

<script>
    import MultiplayerOptionsMixin from '../../../utils/multiplayer/optionsMixin';

    const EXPONENTIATION_ACTION = 'square';
    const ROOT_EXTRACTION_ACTION = 'root';
    const EXPONENTIATION_NAME = 'exponentiation';
    const ROOT_EXTRACTION_NAME = 'rootExtraction';
    const COMMON_PARAMETERS_CHANGED_EVENT = 'commonParametersHasChanged';
    const INFINITE_AMOUNT_OF_EXAMPLES_PARAMETER = 'infinityAmountOfExamples';
    const INITIAL_STATUS = null;

    export default {
        mounted() {
            this.selectedExamplesType = this.examplesTypes.find(type => {
                if (this.commonParams.operations.exponentiation) {
                    return type.action === EXPONENTIATION_ACTION;
                } else if (this.commonParams.operations.rootExtraction) {
                    return type.action === ROOT_EXTRACTION_ACTION;
                }
                return false;
            });
            this.selectedDifficulty = this.difficultyOptions.find(option => {
                return this.commonParams.digitCapacity === option.value;
            });
            this.selectedAmountOfExamples = this.commonParams.amountOfExamples;
            //set first multiselect tags to be in one height with another
            let examplesTypeMultiselectTags = document.querySelector('.multiselect__tags');
            if (examplesTypeMultiselectTags) {
                examplesTypeMultiselectTags.style.cssText = 'padding: 8px 15px';
            }
            this.selectedInfiniteAmountOfExamplesStatus = this.infiniteOptions.find(
                option => option.status === INITIAL_STATUS
            );
        },
        mixins: [MultiplayerOptionsMixin],
        watch: {
            selectedExamplesType: {
                handler(value, oldValue) {
                    if (value) {
                        const isExponentiation = value.action === EXPONENTIATION_ACTION;
                        this.commonParams.operations.exponentiation = isExponentiation;
                        this.commonParams.operations.rootExtraction = !isExponentiation;
                        if (oldValue !== null) {
                            this.$emit(COMMON_PARAMETERS_CHANGED_EVENT, {
                                setOperationsValues: true,
                                names: [EXPONENTIATION_NAME, ROOT_EXTRACTION_NAME],
                                values: [
                                    this.commonParams.operations.exponentiation,
                                    this.commonParams.operations.rootExtraction,
                                ],
                            });
                        }
                    }
                },
                immediate: true,
            },
            selectedDifficulty: {
                handler(value, oldValue) {
                    if (value) {
                        this.commonParams.difficulties = [value.value];
                        if (oldValue !== null) {
                            this.$emit(COMMON_PARAMETERS_CHANGED_EVENT, {
                                name: 'difficulties',
                                value: this.commonParams.difficulties,
                            });
                        }
                    }
                },
                immediate: true,
            },
            selectedAmountOfExamples: {
                handler(value, oldValue) {
                    if (oldValue !== 0) {
                        this.commonParams.amountOfExamples = value;
                        this.$emit(COMMON_PARAMETERS_CHANGED_EVENT, {
                            name: 'amountOfExamples',
                            value: this.commonParams.amountOfExamples,
                        });
                    }
                },
            },
            selectedInfiniteAmountOfExamplesStatus: {
                handler(value, oldValue) {
                    if (oldValue !== null) {
                        this.commonParams.infinityAmountOfExamples = value.status;
                        this.$emit(COMMON_PARAMETERS_CHANGED_EVENT, {
                            name: INFINITE_AMOUNT_OF_EXAMPLES_PARAMETER,
                            value: value.status,
                        });
                    }
                },
            },
        },
        data() {
            return {
                selectedExamplesType: null,
                examplesTypes: [
                    {
                        label: this.$i18n.t('trainer.trainer_gameFormSetting_operationExponentiation'),
                        action: EXPONENTIATION_ACTION,
                    },
                    {
                        label: this.$i18n.t('trainer.trainer_gameFormSetting_operationRootExtraction'),
                        action: ROOT_EXTRACTION_ACTION,
                    },
                ],
                selectedDifficulty: null,
                selectedAmountOfExamples: 0,
                amountsOfExamples: [...Array(101).keys()].slice(1),
                infiniteOptions: [
                    {
                        status: true,
                        label: this.$i18n.t('trainer.trainer_gameFormSettingValue_onAbbreviated'),
                    },
                    {
                        status: false,
                        label: this.$i18n.t('trainer.trainer_gameFormSettingValue_offAbbreviated'),
                    },
                    {
                        status: INITIAL_STATUS,
                        label: this.$i18n.t('trainer.trainer_gameFormSetting_select'),
                    },
                ],
                selectedInfiniteAmountOfExamplesStatus: null,
            };
        },
        computed: {
            infiniteOptionsWithoutInitial() {
                return this.infiniteOptions.filter(option => option.status !== INITIAL_STATUS);
            },
            difficultyOptions() {
                const commonDifficulties = [
                    {
                        value: 1,
                        label: this.$i18n.t('common.common_gameSetting_digitCapacity1'),
                        $isDisabled: false,
                    },
                    {
                        value: 2,
                        label: this.$i18n.t('common.common_gameSetting_digitCapacity2'),
                        $isDisabled: false,
                    },
                    {
                        value: 3,
                        label: this.$i18n.t('common.common_gameSetting_digitCapacity3'),
                        $isDisabled: false,
                    },
                    {
                        value: 4,
                        label: this.$i18n.t('common.common_gameSetting_digitCapacity4'),
                        $isDisabled: false,
                    },
                    {
                        value: 5,
                        label: this.$i18n.t('common.common_gameSetting_digitCapacity5'),
                        $isDisabled: false,
                    },
                ];
                const extraDifficulties = [
                    {
                        value: 6,
                        label: this.$i18n.t('common.common_gameSetting_digitCapacity6'),
                        $isDisabled: false,
                    },
                    {
                        value: 7,
                        label: this.$i18n.t('common.common_gameSetting_digitCapacity7'),
                        $isDisabled: false,
                    },
                    {
                        value: 8,
                        label: this.$i18n.t('common.common_gameSetting_digitCapacity8'),
                        $isDisabled: false,
                    },
                ];
                if (this.slug === 'RootsAndSquares') {
                    return commonDifficulties;
                }
                return [...commonDifficulties, ...extraDifficulties];
            },
        },
    };
</script>

<style scoped lang="scss">
    .multiplayer-game-menu__selectors {
        display: flex;
        width: 80%;
        margin: 20px;
    }
    .selector {
        margin: 0 10px;
        max-height: 44px;
        &:first-child {
            margin-left: 0;
        }
        @media screen and (max-width: 1439px) {
            max-width: 175px;
        }
    }
</style>
